/**
 * Building a form: which questions it asks, in what order, and what happens
 * after somebody answers them.
 *
 * Two halves of one order. The left column holds the fields the form does not
 * ask for and the right column is the form itself, and a field moves between
 * them by one flag -- `enabled` -- rather than by being added to a second list.
 * That is what makes a field taken off the form and put back land where it was
 * instead of at the end.
 *
 * **The preview is the real editors, and nothing in it can be touched.** A form
 * is a column of the same controls the grid opens, so a picture of one drawn by
 * hand would drift away from what visitors actually see the first time an
 * editor changed. Mounting the real thing behind `inert` costs nothing and
 * cannot drift; it is a picture made of the control rather than of paint. The
 * sources are stubbed out for the same reason: nothing in a picture should
 * reach the network, and a link row field would otherwise search its related
 * table on the owner's behalf for a list nobody can open.
 */
import { useEffect, useRef, useState } from 'react'
import { verticalListSortingStrategy } from '@dnd-kit/sortable'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { EyeOff, GripVertical, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { Field, ViewOf } from '@/api/types'
import { SortableList } from '@/shared/components/sortable'
import { useSortableItem } from '@/shared/hooks/use-sortable-item'
import { Button } from '@/shared/ui/button'
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/shared/ui/empty'
import { Input } from '@/shared/ui/input'
import { NativeSelect, NativeSelectOption } from '@/shared/ui/native-select'
import { Skeleton } from '@/shared/ui/skeleton'
import { Switch } from '@/shared/ui/switch'
import { Textarea } from '@/shared/ui/textarea'
import { editorFor } from '../fields/ui/edit'
import { ConditionRow, type Condition } from '../table/condition-row'
import { EditorSourcesProvider, type EditorSources } from '../fields/ui/edit/sources'
import { useFieldContext } from '../fields/ui/context'
import { readFieldOptions } from '../table/field-options'
import { useFieldOptions } from '../table/queries'
import { useUpdateFieldOptions } from '../table/field-options-mutations'
import {
  conditionSources,
  conditionsAfter,
  newCondition,
  splitConditions,
} from './conditions'
import { useUpdateFormConditions } from './condition-mutations'
import { useUpdateForm, type FormChanges } from './form-mutations'
import {
  formLabel,
  orderFormFields,
  reorderOnForm,
  splitFormFields,
  type FormFieldOptions,
} from './options'

/** Where a preview's editors would go for what they need, and find nothing. */
const NOTHING: EditorSources = {
  linkCandidates: () => Promise.resolve([]),
  uploadFile: () => Promise.reject(new Error('a preview cannot upload')),
}

export function FormBuilder({
  view,
  fields,
  tableId,
}: {
  view: ViewOf<'form'>
  fields: readonly Field[]
  tableId: number
}) {
  const options = useFieldOptions(view.id)

  // Nothing until the view has said which fields it asks for. An empty answer
  // and an answer that has not arrived look the same once unwrapped, so a
  // builder drawn before it lands would show an empty form and offer to add
  // every field to it -- on top of an answer still in flight.
  if (options.data === undefined) return <BuilderSkeleton />

  return (
    <Builder
      view={view}
      fields={fields}
      tableId={tableId}
      options={readFieldOptions<FormFieldOptions>(options.data)}
    />
  )
}

function Builder({
  view,
  fields,
  tableId,
  options,
}: {
  view: ViewOf<'form'>
  fields: readonly Field[]
  tableId: number
  options: Record<string, FormFieldOptions>
}) {
  const { t } = useTranslation()
  const update = useUpdateFieldOptions<FormFieldOptions>(view.id)
  const updateForm = useUpdateForm(tableId, view.id)
  const { on, off, refused } = splitFormFields(fields, options)

  // No error handling here: a refused change is put back by the mutation and
  // reported by the global handler, in the API's own words.
  function change(changes: Record<number, Partial<FormFieldOptions>>): void {
    update.mutate(changes)
  }

  function ask(field: Field, enabled: boolean): void {
    change({ [field.id]: { enabled } })
  }

  return (
    <div className="flex h-full min-h-0">
      <div className="w-64 shrink-0 overflow-y-auto border-r p-3">
        <div className="mb-2 flex items-center justify-between gap-2">
          <h2 className="text-sm font-medium">{t('formBuilder.notAsked')}</h2>
          {off.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                change(
                  Object.fromEntries(off.map((field) => [field.id, { enabled: true }])),
                )
              }}
            >
              {t('formBuilder.addAll')}
            </Button>
          )}
        </div>

        {off.length === 0 ? (
          <p className="px-1 text-sm text-muted-foreground">
            {t('formBuilder.everythingAsked')}
          </p>
        ) : (
          <ul aria-label={t('formBuilder.notAsked')} className="flex flex-col gap-1">
            {off.map((field) => (
              <li key={field.id}>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start gap-2"
                  onClick={() => {
                    ask(field, true)
                  }}
                >
                  <Plus className="size-4 shrink-0 opacity-70" />
                  <span className="truncate">{field.name}</span>
                </Button>
              </li>
            ))}
          </ul>
        )}

        {refused.length > 0 && (
          <div className="mt-4 border-t pt-3">
            {/* Named rather than left out. A field missing from both lists reads
                as a bug in the builder; a field listed with the reason reads as
                the answer it is -- the server computes this one, so there is
                nothing for a visitor to fill in. */}
            <h2 className="mb-1 text-sm font-medium">{t('formBuilder.cannotAsk')}</h2>
            <p className="mb-2 text-xs text-muted-foreground">
              {t('formBuilder.cannotAskWhy')}
            </p>
            <ul
              aria-label={t('formBuilder.cannotAsk')}
              className="flex flex-col gap-1 px-2"
            >
              {refused.map((field) => (
                <li key={field.id} className="truncate text-sm text-muted-foreground">
                  {field.name}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto flex max-w-2xl flex-col gap-6 px-6 py-8">
          <header className="flex flex-col gap-2">
            <CommittedInput
              value={view.title ?? ''}
              label={t('formBuilder.title')}
              placeholder={t('formBuilder.titlePlaceholder')}
              className="h-auto border-0 px-0 py-1 text-2xl font-semibold shadow-none focus-visible:ring-0"
              onCommit={(title) => {
                updateForm({ title })
              }}
            />
            <CommittedTextarea
              value={view.intro ?? ''}
              label={t('formBuilder.intro')}
              placeholder={t('formBuilder.introPlaceholder')}
              className="resize-none border-0 px-0 shadow-none focus-visible:ring-0"
              onCommit={(intro) => {
                updateForm({ intro })
              }}
            />
          </header>

          {on.length === 0 ? (
            <Empty className="p-6">
              <EmptyHeader>
                <EmptyTitle>{t('formBuilder.emptyTitle')}</EmptyTitle>
                <EmptyDescription>{t('formBuilder.emptyDescription')}</EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : (
            <ul aria-label={t('formBuilder.asked')} className="flex flex-col gap-4">
              <SortableList
                ids={on.map((field) => field.id)}
                strategy={verticalListSortingStrategy}
                modifiers={[restrictToVerticalAxis]}
                onReorder={(ids) => {
                  // Written across every field of the table, not just the ones
                  // on the form: there is one order and the two columns are
                  // both reading it, so numbering only half of it would leave
                  // the other half sharing places with it.
                  change(
                    Object.fromEntries(
                      reorderOnForm(orderFormFields(fields, options), ids).map(
                        (id, index) => [id, { order: index + 1 }],
                      ),
                    ),
                  )
                }}
              >
                {on.map((field) => (
                  <FormFieldCard
                    key={field.id}
                    field={field}
                    fields={fields}
                    // The questions this one may be conditioned on: the ones
                    // asked before it, and only those.
                    before={conditionSources(on, field)}
                    viewId={view.id}
                    options={options[String(field.id)] ?? {}}
                    onChange={(changes) => {
                      change({ [field.id]: changes })
                    }}
                    onRemove={() => {
                      ask(field, false)
                    }}
                  />
                ))}
              </SortableList>
            </ul>
          )}

          <SubmitControls view={view} onChange={updateForm} />
        </div>
      </div>
    </div>
  )
}

function FormFieldCard({
  field,
  fields,
  before,
  viewId,
  options,
  onChange,
  onRemove,
}: {
  field: Field
  fields: readonly Field[]
  before: readonly Field[]
  viewId: number
  options: FormFieldOptions
  onChange: (changes: Partial<FormFieldOptions>) => void
  onRemove: () => void
}) {
  const { t } = useTranslation()
  const { setNodeRef, style, handleProps, isDragging } = useSortableItem(field.id)

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`rounded-lg border p-3 ${isDragging ? 'opacity-60' : ''}`}
    >
      <div className="flex items-center gap-2">
        <button
          type="button"
          aria-label={t('formBuilder.move', { field: field.name })}
          className="cursor-grab rounded p-0.5 text-muted-foreground hover:bg-muted"
          {...handleProps}
        >
          <GripVertical className="size-4" />
        </button>
        <CommittedInput
          value={options.name ?? ''}
          label={t('formBuilder.label', { field: field.name })}
          placeholder={field.name}
          className="h-8 flex-1 border-0 px-1 font-medium shadow-none focus-visible:ring-0"
          onCommit={(name) => {
            onChange({ name })
          }}
        />
        <Switch
          id={`form-required-${field.id}`}
          size="sm"
          checked={options.required ?? false}
          onCheckedChange={(required) => {
            onChange({ required })
          }}
        />
        <label
          htmlFor={`form-required-${field.id}`}
          className="text-sm text-muted-foreground"
        >
          {t('formBuilder.required')}
        </label>
        <Button
          variant="ghost"
          size="icon"
          className="size-8"
          aria-label={t('formBuilder.remove', { field: field.name })}
          onClick={onRemove}
        >
          <EyeOff className="size-4" />
        </Button>
      </div>

      <CommittedInput
        value={options.description ?? ''}
        label={t('formBuilder.fieldDescription', { field: field.name })}
        placeholder={t('formBuilder.fieldDescriptionPlaceholder')}
        className="mt-1 h-8 border-0 px-1 text-sm shadow-none focus-visible:ring-0"
        onCommit={(description) => {
          onChange({ description })
        }}
      />

      <FieldPreview field={field} fields={fields} options={options} />

      <Conditions
        field={field}
        fields={fields}
        before={before}
        viewId={viewId}
        options={options}
        onChange={onChange}
      />
    </li>
  )
}

/**
 * When to ask this question.
 *
 * Only about the questions before it, which is the rule that keeps a form
 * answerable: a question that depended on one further down would be waiting on
 * an answer nobody has reached, and two of them could wait on each other. The
 * first question therefore has nothing to be conditional on, and says so rather
 * than offering a switch that reveals an empty list.
 */
function Conditions({
  field,
  fields,
  before,
  viewId,
  options,
  onChange,
}: {
  field: Field
  fields: readonly Field[]
  before: readonly Field[]
  viewId: number
  options: FormFieldOptions
  onChange: (changes: Partial<FormFieldOptions>) => void
}) {
  const { t } = useTranslation()
  const context = useFieldContext(fields[0]?.workspace_id)
  const writeConditions = useUpdateFormConditions(viewId)
  const { rows, grouped } = splitConditions(options)
  const on = options.show_when_matching_conditions === true
  const connective = options.condition_type === 'OR' ? 'OR' : 'AND'

  /** Sends the whole list, because the endpoint deletes what it is not sent. */
  function write(next: readonly Condition[]): void {
    writeConditions(field.id, {
      conditions: conditionsAfter(options, next),
      // Echoed for the same reason the grouped conditions are: a group the
      // payload does not name is deleted, and its conditions go with it.
      condition_groups: options.condition_groups ?? [],
    })
  }

  if (before.length === 0) {
    return (
      <p className="mt-3 border-t pt-2 text-xs text-muted-foreground">
        {t('formBuilder.conditionsFirst')}
      </p>
    )
  }

  return (
    <div className="mt-3 border-t pt-2">
      <div className="flex items-center gap-2">
        <Switch
          id={`form-conditions-${field.id}`}
          size="sm"
          checked={on}
          onCheckedChange={(next) => {
            // Switched at once -- it is not a row and has no id to wait for --
            // and turning it on with nothing to show would be a switch that
            // does nothing, so the first condition comes with it.
            onChange({ show_when_matching_conditions: next })
            if (next && rows.length === 0) {
              const first = newCondition(before)
              if (first !== null) write([first])
            }
          }}
        />
        <label
          htmlFor={`form-conditions-${field.id}`}
          className="text-sm text-muted-foreground"
        >
          {t('formBuilder.conditionsOn')}
        </label>
      </div>

      {on && (
        <>
          {rows.length > 0 && (
            <ul
              aria-label={t('formBuilder.conditionsFor', { field: field.name })}
              className="mt-2 flex flex-col gap-1"
            >
              {rows.map((condition, index) => (
                <ConditionRow
                  key={`${condition.id}-${index}`}
                  filter={condition}
                  position={index}
                  connective={connective}
                  fields={before}
                  context={context}
                  onConnective={(next) => {
                    onChange({ condition_type: next })
                  }}
                  onChange={(changes) => {
                    write(
                      rows.map((held, at) =>
                        at === index ? { ...held, ...changes } : held,
                      ),
                    )
                  }}
                  onRemove={() => {
                    write(rows.filter((_, at) => at !== index))
                  }}
                />
              ))}
            </ul>
          )}

          {grouped.length > 0 && (
            <p className="mt-2 text-xs text-muted-foreground">
              {t('formBuilder.conditionsGrouped', { count: grouped.length })}
            </p>
          )}

          <Button
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={() => {
              const next = newCondition(before)
              if (next !== null) write([...rows, next])
            }}
          >
            {t('formBuilder.conditionAdd')}
          </Button>
        </>
      )}
    </div>
  )
}

/**
 * What the visitor will be given, drawn with the control they will be given.
 *
 * `inert` is the behaviour -- nothing in here is reachable by pointer, keyboard
 * or the accessibility tree -- and `aria-hidden` is the same statement in the
 * form every browser already understands. Both, because this is a picture: the
 * question above it has already been announced, and announcing it again through
 * a control nobody can use would be announcing it twice.
 */
function FieldPreview({
  field,
  fields,
  options,
}: {
  field: Field
  fields: readonly Field[]
  options: FormFieldOptions
}) {
  const context = useFieldContext(fields[0]?.workspace_id)
  const editor = editorFor(field)
  if (editor === null) return null

  return (
    <div inert aria-hidden className="mt-2 opacity-70">
      <EditorSourcesProvider sources={NOTHING}>
        <editor.Component
          field={field}
          context={context}
          value={editor.toDraft(field, null)}
          label={formLabel(field, options)}
          autoFocus={false}
          onValueChange={() => {
            // A picture of a control. There is nowhere for a value to go.
          }}
          onKeyDown={() => {
            // Unreachable: nothing inside an inert subtree takes a key.
          }}
        />
      </EditorSourcesProvider>
    </div>
  )
}

/** The button's words, and where the visitor goes once they have pressed it. */
function SubmitControls({
  view,
  onChange,
}: {
  view: ViewOf<'form'>
  onChange: (changes: FormChanges) => void
}) {
  const { t } = useTranslation()
  const action = view.submit_action ?? 'MESSAGE'

  return (
    <div className="flex flex-col gap-4 border-t pt-6">
      <Labelled label={t('formBuilder.submitText')}>
        <CommittedInput
          value={view.submit_text ?? ''}
          label={t('formBuilder.submitText')}
          placeholder={t('form.submit')}
          onCommit={(submit_text) => {
            onChange({ submit_text })
          }}
        />
      </Labelled>

      <Labelled label={t('formBuilder.afterSubmit')}>
        <NativeSelect
          aria-label={t('formBuilder.afterSubmit')}
          value={action}
          onChange={(event) => {
            onChange({
              submit_action: event.target.value === 'REDIRECT' ? 'REDIRECT' : 'MESSAGE',
            })
          }}
        >
          <NativeSelectOption value="MESSAGE">
            {t('formBuilder.showMessage')}
          </NativeSelectOption>
          <NativeSelectOption value="REDIRECT">
            {t('formBuilder.goToUrl')}
          </NativeSelectOption>
        </NativeSelect>
      </Labelled>

      {action === 'MESSAGE' ? (
        <Labelled label={t('formBuilder.theMessage')}>
          <CommittedTextarea
            value={view.submit_action_message ?? ''}
            label={t('formBuilder.theMessage')}
            placeholder={t('form.thanks')}
            onCommit={(submit_action_message) => {
              onChange({ submit_action_message })
            }}
          />
        </Labelled>
      ) : (
        <Labelled label={t('formBuilder.theUrl')} hint={t('formBuilder.theUrlHint')}>
          <CommittedInput
            value={view.submit_action_redirect_url ?? ''}
            label={t('formBuilder.theUrl')}
            type="url"
            onCommit={(submit_action_redirect_url) => {
              onChange({ submit_action_redirect_url })
            }}
          />
        </Labelled>
      )}
    </div>
  )
}

function Labelled({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-medium">{label}</span>
      {children}
      {hint !== undefined && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  )
}

/**
 * A box that holds what is being typed and sends it once.
 *
 * Sent on the way out rather than after a pause, which is the opposite of the
 * choice the search box and the filter values make -- and for the opposite
 * reason. Those change which rows the view contains, so a pause is the longest
 * they can wait; nothing here changes anything but its own words, and the box
 * is already showing them. A request per pause would be several requests, one
 * undo entry each, for one sentence.
 *
 * It also sends on the way out of the *page*: a value typed and then abandoned
 * by switching views loses its blur, because an element removed while focused
 * does not reliably get one.
 */
function useCommitted(
  value: string,
  onCommit: (next: string) => void,
): { draft: string; onChange: (next: string) => void; commit: () => void } {
  const [draft, setDraft] = useState(value)
  // What the server was last told, which is what says whether there is
  // anything to send -- and what tells a change arriving from elsewhere apart
  // from the echo of this box's own change.
  const sent = useRef(value)
  const held = useRef(value)

  if (value !== sent.current) {
    sent.current = value
    held.current = value
    setDraft(value)
  }

  const commit = (): void => {
    if (held.current === sent.current) return
    sent.current = held.current
    onCommit(held.current)
  }

  // Held in a ref and read on the way out. A cleanup with `commit` in its
  // dependencies would run on every keystroke, which is exactly the
  // request-per-letter this arrangement exists to avoid; a cleanup with no
  // dependencies would close over the first render's `commit` and send the
  // value the box started with.
  const latest = useRef(commit)
  useEffect(() => {
    latest.current = commit
  })
  useEffect(
    () => () => {
      latest.current()
    },
    [],
  )

  return {
    draft,
    onChange: (next) => {
      held.current = next
      setDraft(next)
    },
    commit,
  }
}

function CommittedInput({
  value,
  label,
  onCommit,
  ...rest
}: {
  value: string
  label: string
  onCommit: (next: string) => void
} & Omit<React.ComponentProps<typeof Input>, 'value' | 'onChange' | 'onBlur'>) {
  const { draft, onChange, commit } = useCommitted(value, onCommit)

  return (
    <Input
      {...rest}
      aria-label={label}
      value={draft}
      onChange={(event) => {
        onChange(event.target.value)
      }}
      onBlur={commit}
      onKeyDown={(event) => {
        // Enter means "that is the answer" in a one-line box. Without it the
        // only way to send is to click somewhere else, which is not obvious.
        if (event.key === 'Enter') event.currentTarget.blur()
      }}
    />
  )
}

function CommittedTextarea({
  value,
  label,
  onCommit,
  ...rest
}: {
  value: string
  label: string
  onCommit: (next: string) => void
} & Omit<React.ComponentProps<typeof Textarea>, 'value' | 'onChange' | 'onBlur'>) {
  const { draft, onChange, commit } = useCommitted(value, onCommit)

  return (
    <Textarea
      {...rest}
      aria-label={label}
      rows={2}
      value={draft}
      onChange={(event) => {
        onChange(event.target.value)
      }}
      onBlur={commit}
    />
  )
}

function BuilderSkeleton() {
  return (
    <div className="space-y-3 p-6">
      <Skeleton className="h-8 w-1/3" />
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-24 w-full" />
    </div>
  )
}
